import type { Client } from '../services/api'

export const RESUME_FILE_ACCEPT = '.pdf,.doc,.docx'
export const RESUME_FILE_MAX_BYTES = 10 * 1024 * 1024

const WORD_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

export function isPdf(contentType: string | undefined): boolean {
  return contentType === 'application/pdf'
}

export function isWordDocument(contentType: string | undefined): boolean {
  return !!contentType && WORD_TYPES.includes(contentType)
}

// Some browsers report an empty type for a .docx, so the extension decides when
// the type says nothing. A type that says something else is believed.
function resumeFileKind(file: { name: string; type: string }): 'pdf' | 'word' | null {
  if (isPdf(file.type)) return 'pdf'
  if (isWordDocument(file.type)) return 'word'
  if (file.type && file.type !== 'application/octet-stream') return null
  const extension = file.name.toLowerCase().split('.').pop()
  if (extension === 'pdf') return 'pdf'
  if (extension === 'doc' || extension === 'docx') return 'word'
  return null
}

/** Why this file cannot be the client's resume, or null when it can. */
export function resumeFileError(file: { name: string; type: string; size: number }): string | null {
  if (file.size > RESUME_FILE_MAX_BYTES) {
    return 'The file did not upload. Check the file size is under 10MB and try again.'
  }
  if (!resumeFileKind(file)) {
    return 'Upload the resume as a PDF or Word document (.pdf, .doc or .docx).'
  }
  return null
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Whether the resume is in front of the client or signed off, which is when its
 * source and file stop being changeable. Mirrors isResumeLive in the
 * publish-content lambda, which is the check that actually enforces it; this one
 * only decides whether the controls are offered. A record from before drafts
 * existed has no resumeIsDraft and its content is visible, so only an explicit
 * draft counts as hidden.
 */
export function isResumeLive(
  client: Pick<Client, 'resumeIsDraft' | 'resumeContent' | 'resumeApproved' | 'resumeAttachmentId'>
): boolean {
  if (client.resumeIsDraft === true) return false
  return !!client.resumeContent || client.resumeApproved === true || !!client.resumeAttachmentId
}
